import { useMemo } from "react";

import { ApiErrorCode, getApiErrorCode, useAlertContext, useFormikWithDirtyForm } from "@shared";
import { useQueryClient } from "@tanstack/react-query";

import { CreateDutyScheduleRequest, dutySchedulesApi } from "@/api";
import { withLocalErrorHandling } from "@/api/middleware/errorMiddleware";
import { DutyScheduleFormValues } from "@/pages/duty-schedule/DutySchedule.types";
import {
  dutyScheduleValidationSchema,
  emptyDutyScheduleFormValues,
} from "@/pages/duty-schedule/components/create-dialog/DutyScheduleForm.utils";
import { QueryKey } from "@/query/Query";
import { useTranslation } from "@/translations";

export function useDutyScheduleCreation(onClose: () => void) {
  const { t } = useTranslation();
  const { showSuccess, showError } = useAlertContext();
  const queryClient = useQueryClient();

  const validationSchema = useMemo(() => dutyScheduleValidationSchema(t), [t]);

  const formik = useFormikWithDirtyForm<DutyScheduleFormValues>({
    initialValues: emptyDutyScheduleFormValues(),
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    onSubmit: async ({ name, periodStart, periodEnd }) => {
      // Narrowing only — the schema already requires both dates.
      if (periodStart == null || periodEnd == null) {
        return;
      }

      const request: CreateDutyScheduleRequest = { name, periodStart, periodEnd };

      try {
        // Handled locally so the overlap rejection is reported in German rather than as the
        // backend's English ProblemDetails.detail, which the global toast would show verbatim.
        await dutySchedulesApi.createDutySchedule({ createDutyScheduleRequest: request }, withLocalErrorHandling);
      } catch (error) {
        const isOverlap = (await getApiErrorCode(error)) === ApiErrorCode.DutySchedulePeriodOverlaps;
        showError(isOverlap ? t("dutySchedule.create.validation.periodOverlaps") : t("common.requestError"));
        return;
      }

      void queryClient.invalidateQueries({ queryKey: [QueryKey.DutySchedules] });
      showSuccess(t("dutySchedule.create.success"));
      onClose();
    },
  });

  return { formik };
}
